// src/pages/sign/SignContract.js
// Public, no-auth route at /sign/:token. The seller (or other signer) lands here
// from the email link, reviews the contract, consents to e-sign, draws/types
// their signature, and submits. Audit events are captured throughout.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { renderContractHtml } from '../../lib/contracts/render';
import { CONTRACT_META } from '../../lib/contracts/templates';
import SignaturePad from '../../components/sign/SignaturePad';
import Logo from '../../components/Logo';

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const GOLD_LITE = '#E2C47A';
const CREAM = '#FAF7F2';
const WHITE = '#FFFFFF';
const MID = '#5A5A5A';
const LIGHT = '#9A9A9A';
const BORDER = 'rgba(107,30,46,0.1)';

export default function SignContract() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [stage, setStage] = useState('loading'); // loading | review | sign | submitting | done | error
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [agreedConsent, setAgreedConsent] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signatureImage, setSignatureImage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const startedAtRef = useRef(Date.now());
  const iframeRef = useRef(null);

  // Inject Google Fonts for the cursive signature option
  useEffect(() => {
    const id = 'hiqa-sign-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;1,500&family=DM+Sans:wght@400;500;700&family=Great+Vibes&display=swap';
    document.head.appendChild(link);
  }, []);

  // Fetch the signature request from the cloud
  useEffect(() => {
    if (!token) { setStage('error'); setError('Missing signing token.'); return; }
    (async () => {
      try {
        const fn = httpsCallable(functions, 'getSignatureRequest');
        const res = await fn({ token });
        const data = res.data;
        if (!data?.ok) throw new Error(data?.message || 'Could not load this document.');
        setRequest(data.request);
        setSignerName(data.request?.parties?.[0]?.name || '');
        setStage(data.request?.status === 'signed' ? 'done' : 'review');
        // record the "viewed" event (non-fatal if it errors)
        httpsCallable(functions, 'recordSignatureEvent')({ token, event: 'viewed' }).catch(() => {});
      } catch (e) {
        setStage('error');
        setError(e.message || 'This signing link is invalid, expired, or has already been completed.');
      }
    })();
  }, [token]);

  // Render the contract HTML once we have the request
  const contractHtml = useMemo(() => {
    if (!request) return '';
    const meta = CONTRACT_META[request.contractType];
    return renderContractHtml({
      type: request.contractType,
      fields: request.fields || {},
      documentId: token.slice(0, 8).toUpperCase(),
      parties: (meta?.signers || []).map(p => ({
        ...p,
        name: request.fields?.[`${p.role}Name`] || ''
      }))
    });
  }, [request, token]);

  // Track scroll-to-bottom inside the iframe so we can require it before signing
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || stage !== 'review') return;
    const wireScroll = () => {
      const win = iframe.contentWindow;
      const doc = iframe.contentDocument;
      if (!win || !doc) return;
      const onScroll = () => {
        const max = doc.documentElement.scrollHeight - win.innerHeight;
        if (max <= 0 || win.scrollY / max > 0.85) {
          setScrolledToBottom(true);
          httpsCallable(functions, 'recordSignatureEvent')({ token, event: 'scrolled_to_bottom' }).catch(() => {});
          win.removeEventListener('scroll', onScroll);
        }
      };
      win.addEventListener('scroll', onScroll, { passive: true });
      onScroll(); // in case the doc already fits
    };
    iframe.addEventListener('load', wireScroll);
    return () => iframe.removeEventListener('load', wireScroll);
  }, [stage, token, contractHtml]);

  const submit = async () => {
    setSubmitError('');
    if (!agreedConsent) { setSubmitError('Please confirm your consent to use electronic signatures.'); return; }
    if (!signerName.trim()) { setSubmitError('Please confirm your full legal name.'); return; }
    if (!signatureImage) { setSubmitError('Please add your signature above.'); return; }
    setStage('submitting');
    try {
      const fn = httpsCallable(functions, 'submitSignature');
      const res = await fn({
        token,
        signerName: signerName.trim(),
        signatureImage,
        signingDurationMs: Date.now() - startedAtRef.current,
        scrolledToBottom
      });
      if (!res.data?.ok) throw new Error(res.data?.message || 'Submission failed.');
      setStage('done');
    } catch (e) {
      setStage('sign');
      setSubmitError(e.message || 'Could not submit your signature. Please try again.');
    }
  };

  // ── render ─────────────────────────────────────────────────────────
  if (stage === 'loading') return <SimplePage><div style={{ textAlign: 'center', padding: 40 }}>Loading your document…</div></SimplePage>;
  if (stage === 'error')   return <ErrorState message={error} />;
  if (stage === 'done')    return <DoneState request={request} />;

  const meta = request ? CONTRACT_META[request.contractType] : null;
  const propertyLine = request?.fields?.subjectProperty || request?.fields?.propertyAddress || '';
  const requestedBy = request?.agentName || request?.fields?.buyerName || 'HomeIQa';

  return (
    <div style={S.page}>
      <header style={S.header}>
        <div style={S.headerInner}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <Logo />
            <div style={S.divider} />
            <div>
              <div style={S.eyebrow}>IQ Sign · Document for signature</div>
              <div style={S.docTitle}>{meta?.label}</div>
            </div>
          </div>
          <div style={S.trust}>
            <span style={S.trustDot} /> Secure · ESIGN Act compliant
          </div>
        </div>
        {propertyLine && (
          <div style={S.subhead}>
            <div><span style={S.subLabel}>Property</span> {propertyLine}</div>
            {requestedBy && <div><span style={S.subLabel}>Requested by</span> {requestedBy}</div>}
          </div>
        )}
        <div style={S.steps}>
          <StepDot active={stage === 'review'} done={stage !== 'review'} label="1. Review" />
          <span style={S.stepLine} />
          <StepDot active={stage === 'sign' || stage === 'submitting'} done={false} label="2. Sign" />
          <span style={S.stepLine} />
          <StepDot active={false} done={false} label="3. Done" />
        </div>
      </header>

      <main style={S.main}>
        <div style={S.docFrame}>
          <iframe
            ref={iframeRef}
            srcDoc={contractHtml}
            title="Contract preview"
            style={S.iframe}
          />
        </div>

        {stage === 'review' ? (
          <div style={S.actionCard}>
            <div style={S.actionLeft}>
              <div style={S.actionTitle}>{scrolledToBottom ? 'Ready to sign' : 'Please review the full document'}</div>
              <div style={S.actionSub}>
                {scrolledToBottom
                  ? 'Click continue to enter your signature and confirm your identity.'
                  : 'Scroll to the bottom of the document to enable the continue button.'}
              </div>
            </div>
            <button
              onClick={() => setStage('sign')}
              disabled={!scrolledToBottom}
              style={{ ...S.primaryBtn, ...(scrolledToBottom ? {} : S.btnDisabled) }}
            >
              Continue to sign →
            </button>
          </div>
        ) : (
          <div style={S.signCard}>
            <div style={S.signEyebrow}>Your signature</div>
            <h2 style={S.signTitle}>Sign and submit your <em style={{ fontStyle: 'italic', color: BURG }}>{meta?.label}</em></h2>

            <div style={S.consentBox}>
              <label style={S.consentLabel}>
                <input type="checkbox" checked={agreedConsent} onChange={e => setAgreedConsent(e.target.checked)} style={S.consentCheck} />
                <span>
                  I agree to use electronic records and electronic signatures for this transaction, and I understand
                  that my electronic signature has the same legal effect as a handwritten signature under the
                  E-SIGN Act and applicable state law.
                </span>
              </label>
            </div>

            <label style={S.fieldLabel}>Full legal name
              <input type="text" value={signerName} onChange={e => setSignerName(e.target.value)} style={S.fieldInput} placeholder="Your full legal name" />
            </label>

            <SignaturePad defaultName={signerName} onChange={setSignatureImage} />

            {submitError && <div style={S.errorBox}>{submitError}</div>}

            <div style={S.submitRow}>
              <button onClick={() => setStage('review')} type="button" style={S.ghostBtn}>← Back to document</button>
              <button onClick={submit} type="button" disabled={stage === 'submitting'} style={S.primaryBtn}>
                {stage === 'submitting' ? 'Submitting signature…' : 'Sign and submit'}
              </button>
            </div>

            <div style={S.audit}>
              <strong style={{ color: BURG_DK }}>What we record on submission:</strong>
              {' '}timestamp, your IP address, your device's user agent, the time you spent reviewing,
              and confirmation that you scrolled to the end of the document. This audit log is attached
              to the signed PDF and is admissible in court under the ESIGN Act.
            </div>
          </div>
        )}
      </main>

      <footer style={S.footer}>
        <span>© {new Date().getFullYear()} HomeIQa</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={S.footerDot} /> Bank-level TLS · Token-gated access · Audit logged
        </span>
      </footer>
    </div>
  );
}

const StepDot = ({ active, done, label }) => (
  <div style={S.step}>
    <div style={{ ...S.stepCircle, ...(active ? S.stepActive : done ? S.stepDone : {}) }}>
      {done ? '✓' : ''}
    </div>
    <div style={{ ...S.stepLabel, color: active ? BURG_DK : LIGHT }}>{label}</div>
  </div>
);

const DoneState = ({ request }) => (
  <SimplePage>
    <div style={{ textAlign: 'center', padding: '12px 0' }}>
      <div style={S.bigCheck}>✓</div>
      <h1 style={S.doneTitle}>Thank you — your <em style={{ fontStyle: 'italic', color: BURG }}>signature</em> is recorded.</h1>
      <p style={S.doneBody}>
        A copy of the signed {CONTRACT_META[request?.contractType]?.label || 'document'} has been emailed to you and to the requesting party.
        You may close this window.
      </p>
      <div style={S.doneCert}>
        <div style={S.certEyebrow}>Signing certificate</div>
        {request?.parties?.map((p, i) => (
          <div key={i} style={{ fontSize: 13, color: BURG_DK, padding: '4px 0' }}>
            <strong>{p.label || p.role}:</strong> {p.name} · {p.signedAt ? new Date(p.signedAt).toLocaleString() : 'pending'}
          </div>
        ))}
      </div>
    </div>
  </SimplePage>
);

const ErrorState = ({ message }) => (
  <SimplePage>
    <div style={{ textAlign: 'center' }}>
      <h1 style={{ ...S.doneTitle, color: '#991B1B' }}>This signing link can't be opened.</h1>
      <p style={S.doneBody}>{message}</p>
      <p style={{ ...S.doneBody, fontSize: 12 }}>
        If you believe this is a mistake, please reply to the original email so the sender can re-issue the document.
      </p>
    </div>
  </SimplePage>
);

const SimplePage = ({ children }) => (
  <div style={S.page}>
    <header style={{ ...S.header, paddingBottom: 18 }}>
      <div style={S.headerInner}>
        <Logo />
        <div style={S.trust}><span style={S.trustDot} /> Secure signing</div>
      </div>
    </header>
    <main style={{ ...S.main, paddingTop: 60 }}>
      <div style={S.simpleCard}>{children}</div>
    </main>
  </div>
);

const S = {
  page: { minHeight: '100vh', background: 'linear-gradient(180deg, #FAF7F2 0%, #F2EBE0 100%)', fontFamily: "'DM Sans', sans-serif", color: '#1C1C1C' },
  header: { background: '#fff', borderBottom: `1px solid ${BORDER}`, padding: '20px 32px 0', position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 1px 0 rgba(0,0,0,0.02)' },
  headerInner: { maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' },
  divider: { width: 1, height: 28, background: BORDER },
  eyebrow: { fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, marginBottom: 2, fontWeight: 500 },
  docTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, color: BURG_DK, lineHeight: 1.1 },
  trust: { display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, color: MID, fontWeight: 500, letterSpacing: '0.04em' },
  trustDot: { width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', boxShadow: '0 0 0 3px rgba(29,158,117,0.18)' },
  subhead: { maxWidth: 1100, margin: '14px auto 0', display: 'flex', gap: 36, flexWrap: 'wrap', fontSize: 13, color: MID, paddingBottom: 14, borderBottom: `1px solid ${BORDER}` },
  subLabel: { fontSize: 10, color: LIGHT, textTransform: 'uppercase', letterSpacing: '0.1em', marginRight: 6 },
  steps: { maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '14px 0 16px' },
  step: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 },
  stepCircle: { width: 22, height: 22, borderRadius: '50%', background: CREAM, border: `1.5px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: LIGHT, fontWeight: 700 },
  stepActive: { background: BURG, borderColor: BURG, color: '#fff' },
  stepDone: { background: GOLD, borderColor: GOLD, color: BURG_DK },
  stepLabel: { fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 500 },
  stepLine: { width: 60, height: 1, background: BORDER },
  main: { maxWidth: 1100, margin: '0 auto', padding: '24px 24px 80px' },
  docFrame: { background: '#E8E0D5', borderRadius: 8, overflow: 'hidden', border: `1px solid ${BORDER}`, marginBottom: 18 },
  iframe: { display: 'block', width: '100%', height: '760px', border: 'none', background: '#E8E0D5' },
  actionCard: { background: WHITE, border: `1px solid ${BORDER}`, borderLeft: `3px solid ${GOLD}`, borderRadius: 6, padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap', boxShadow: '0 6px 24px rgba(74,18,32,0.06)' },
  actionLeft: { flex: 1, minWidth: 240 },
  actionTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 500, color: BURG_DK, marginBottom: 4 },
  actionSub: { fontSize: 13, color: MID, lineHeight: 1.55 },
  signCard: { background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '32px 36px', boxShadow: '0 12px 40px rgba(74,18,32,0.08)' },
  signEyebrow: { fontSize: 10, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, borderBottom: `1px solid ${GOLD}`, paddingBottom: 4, display: 'inline-block', marginBottom: 14 },
  signTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 500, color: BURG_DK, lineHeight: 1.18, letterSpacing: '-0.3px', margin: '0 0 22px' },
  consentBox: { background: CREAM, border: `1px solid ${BORDER}`, borderLeft: `3px solid ${GOLD}`, borderRadius: '0 4px 4px 0', padding: '14px 16px', marginBottom: 22 },
  consentLabel: { display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 13, color: BURG_DK, lineHeight: 1.55, cursor: 'pointer' },
  consentCheck: { width: 18, height: 18, marginTop: 2, accentColor: BURG, cursor: 'pointer', flexShrink: 0 },
  fieldLabel: { display: 'block', marginBottom: 18, fontSize: 11, fontWeight: 500, color: MID, letterSpacing: '0.04em', textTransform: 'uppercase' },
  fieldInput: { display: 'block', width: '100%', marginTop: 6, padding: '11px 14px', border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: BURG_DK, outline: 'none', textTransform: 'none', letterSpacing: 'normal' },
  errorBox: { padding: '11px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 4, color: '#991B1B', fontSize: 12, marginTop: 16 },
  submitRow: { display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 24, alignItems: 'center', flexWrap: 'wrap' },
  ghostBtn: { background: 'transparent', border: `1px solid ${BORDER}`, color: BURG, padding: '10px 18px', fontSize: 13, borderRadius: 4, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  primaryBtn: { background: BURG, color: WHITE, border: `1px solid ${GOLD}`, padding: '12px 26px', fontSize: 14, fontWeight: 500, borderRadius: 4, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em', boxShadow: '0 4px 12px rgba(74,18,32,0.18)' },
  btnDisabled: { opacity: 0.45, cursor: 'not-allowed', boxShadow: 'none' },
  audit: { fontSize: 11, color: LIGHT, lineHeight: 1.6, marginTop: 22, padding: '12px 14px', background: CREAM, borderRadius: 4, border: `1px solid ${BORDER}` },
  footer: { maxWidth: 1100, margin: '0 auto', padding: '18px 32px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: LIGHT, borderTop: `1px solid ${BORDER}`, flexWrap: 'wrap', gap: 12 },
  footerDot: { width: 6, height: 6, borderRadius: '50%', background: GOLD },
  simpleCard: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '40px 44px', boxShadow: '0 12px 40px rgba(74,18,32,0.08)' },
  bigCheck: { width: 64, height: 64, borderRadius: '50%', background: GOLD, color: BURG_DK, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, fontWeight: 700, margin: '0 auto 18px' },
  doneTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 500, color: BURG_DK, letterSpacing: '-0.4px', lineHeight: 1.2, margin: '0 0 14px' },
  doneBody: { fontSize: 14, color: MID, lineHeight: 1.6, margin: '0 0 14px' },
  doneCert: { background: CREAM, border: `1px solid ${BORDER}`, borderLeft: `3px solid ${GOLD}`, padding: '14px 18px', textAlign: 'left', maxWidth: 480, margin: '20px auto 0', borderRadius: '0 4px 4px 0' },
  certEyebrow: { fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, marginBottom: 8, fontWeight: 500 }
};
